import { useEffect, useState } from 'react';
import { api, ApiError, type StatsResponse } from './api/client';

type Period = 'day' | 'week' | 'month' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  day: 'За сутки',
  week: 'За 7 дней',
  month: 'За 30 дней',
  all: 'Всё время',
};

export function Dashboard({ onClose }: { onClose: () => void }) {
  const [period, setPeriod] = useState<Period>('week');
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .adminStats(period)
      .then((r) => setData(r))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="min-h-screen bg-gray-50 pb-8 px-3 sm:px-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 0.75rem)' }}>
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center justify-between gap-3 sticky top-0 bg-gray-50/95 backdrop-blur z-10 py-2 -mx-3 sm:-mx-4 px-3 sm:px-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">📊 Статистика использования</h1>
            <p className="text-xs sm:text-sm text-gray-500">ASR-минуты и оценка трат на Yandex SpeechKit</p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-100 min-h-[44px]"
          >
            ← Назад
          </button>
        </header>

        {/* Selector периода */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={
                'flex-1 py-2 rounded-lg text-sm font-medium transition-colors min-h-[40px] ' +
                (period === p ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900')
              }
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        {loading && <div className="text-center text-gray-400 py-12">Загрузка…</div>}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Ключевая метрика — сэкономленное время */}
            <TimeSavedCard
              minutes={data.totals.time_saved_minutes}
              sessions={data.totals.structure_sessions}
              baseline={data.baseline_minutes_per_protocol}
              withTool={data.with_tool_minutes_per_protocol}
            />

            {/* KPI-карточки */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              <Kpi label="Транскрипций" value={data.totals.transcribe_sessions.toString()} />
              <Kpi label="Структурирований" value={data.totals.structure_sessions.toString()} />
              <Kpi label="Минут аудио" value={data.totals.asr_minutes.toFixed(1)} />
              <Kpi label="₽ потрачено" value={`~${data.totals.asr_cost_rub.toFixed(2)}`} />
            </div>

            <div className="text-xs text-gray-500 px-1">
              Тариф: {data.asr_rate_per_minute_rub.toFixed(2)} ₽/мин (Yandex SpeechKit async).
              Оценка только по ASR — LLM-расходы (GigaChat) ~5% от этой суммы.
            </div>

            {/* По юзерам */}
            <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <h2 className="text-sm uppercase text-gray-500 font-semibold px-4 pt-3 pb-2">По пользователям</h2>
              {data.by_user.length === 0 ? (
                <div className="text-gray-400 text-sm px-4 py-6">Нет данных за период.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Пользователь</th>
                      <th className="text-right px-3 py-2">Сессий</th>
                      <th className="text-right px-3 py-2">Минут</th>
                      <th className="text-right px-3 py-2">₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_user.map((u) => (
                      <tr key={u.telegram_id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{u.user_name}</div>
                          <div className="text-xs text-gray-500">
                            TG: <code>{u.telegram_id}</code>
                          </div>
                        </td>
                        <td className="text-right px-3 py-2 tabular-nums">
                          {u.transcribe_sessions}
                          <span className="text-gray-400"> / {u.structure_sessions}</span>
                        </td>
                        <td className="text-right px-3 py-2 tabular-nums">{u.asr_minutes.toFixed(1)}</td>
                        <td className="text-right px-3 py-2 tabular-nums font-medium">
                          {u.asr_cost_rub.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="text-xs text-gray-400 px-3 py-2 border-t border-gray-100">
                Сессий: транскрипции / структурирования
              </div>
            </section>

            {/* По дням */}
            <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <h2 className="text-sm uppercase text-gray-500 font-semibold px-4 pt-3 pb-2">По дням</h2>
              {data.by_day.length === 0 ? (
                <div className="text-gray-400 text-sm px-4 py-6">Нет данных за период.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Дата</th>
                      <th className="text-right px-3 py-2">Транскрипций</th>
                      <th className="text-right px-3 py-2">Минут</th>
                      <th className="text-right px-3 py-2">₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_day.map((d) => (
                      <tr key={d.day} className="border-t border-gray-100">
                        <td className="px-3 py-2 tabular-nums">{d.day}</td>
                        <td className="text-right px-3 py-2 tabular-nums">{d.transcribe_sessions}</td>
                        <td className="text-right px-3 py-2 tabular-nums">{d.asr_minutes.toFixed(1)}</td>
                        <td className="text-right px-3 py-2 tabular-nums font-medium">
                          {d.asr_cost_rub.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <div className="text-xs text-gray-400 text-center pt-2">
              Данные — metadata из usage_events. Контент сессий не хранится (см. ADR-004).
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className={
        'rounded-xl p-3 border ' +
        (highlight ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200')
      }
    >
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={'text-xl sm:text-2xl font-bold mt-1 ' + (highlight ? 'text-blue-900' : 'text-gray-900')}>
        {value}
      </div>
    </div>
  );
}

function TimeSavedCard({
  minutes,
  sessions,
  baseline,
  withTool,
}: {
  minutes: number;
  sessions: number;
  baseline: number;
  withTool: number;
}) {
  const perSession = Math.max(0, baseline - withTool);
  const hours = minutes / 60;
  const bigValue = hours >= 1 ? `${hours.toFixed(1)} ч` : `${Math.round(minutes)} мин`;
  const empty = sessions === 0;

  return (
    <section
      className={
        'rounded-2xl p-5 sm:p-6 border-2 ' +
        (empty
          ? 'bg-gray-50 border-gray-200'
          : 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200')
      }
    >
      <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700/80">
        Сэкономлено времени
      </div>
      <div
        className={
          'text-4xl sm:text-5xl font-bold mt-1 tabular-nums ' +
          (empty ? 'text-gray-400' : 'text-emerald-900')
        }
      >
        {empty ? '—' : `~${bigValue}`}
      </div>
      <div className="text-sm text-gray-600 mt-2">
        {empty
          ? 'Пока нет структурированных приёмов за этот период.'
          : `${sessions} ${plural(sessions, 'приём', 'приёма', 'приёмов')} × ${perSession.toFixed(0)} мин на каждом.`}
      </div>
      <div className="text-xs text-gray-500 mt-3 pt-3 border-t border-emerald-200/60">
        Baseline: {baseline.toFixed(0)} мин ручного оформления → {withTool.toFixed(0)} мин с инструментом.
        Настраивается в env.
      </div>
    </section>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
